import { describeApiError } from "./errors";

export interface BulkItemFailure {
    readonly label: string;
    readonly message: string;
}

/**
 * Runs a mutation once per item, in order. One failure does not stop
 * the rest — the caller shows a summary and refreshes the list so the
 * rows that succeeded are already updated.
 */
export async function runBulk<T>(
    items: readonly T[],
    run: (item: T) => Promise<void>,
    labelOf: (item: T) => string,
): Promise<readonly BulkItemFailure[]> {
    const failures: BulkItemFailure[] = [];
    for (const item of items) {
        try {
            await run(item);
        } catch (error) {
            failures.push({ label: labelOf(item), message: describeApiError(error) });
        }
    }
    return failures;
}

export function summarizeBulkFailures(failures: readonly BulkItemFailure[]): string | null {
    if (failures.length === 0) return null;
    const preview = failures
        .slice(0, 3)
        .map((failure) => `${failure.label}: ${failure.message}`)
        .join(" ");
    const more = failures.length > 3 ? ` (+${failures.length - 3} more)` : "";
    return `${failures.length} could not be updated. ${preview}${more}`;
}
